import moment from 'moment';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DayInput from 'components/DateInput/DayInput';
import TimeInput from 'components/DateInput/TimeInput';
import useCreateNewRoomDataContext from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/useCreateNewRoomDataContext';
import { RoomFormType } from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/utils';
import { PickByValue } from 'utils/types';

import { getMinimumAllowedDateAndTime } from './utils';

type FormDateKeys = keyof PickByValue<RoomFormType, Date>;

interface DateTimeFieldProps {
  control: Control<RoomFormType, undefined>;
  dateName: FormDateKeys;
  timeName: FormDateKeys;
  label: string;
}

const DateTimeField = ({ control, dateName, timeName, label }: DateTimeFieldProps) => {
  const { t } = useTranslation();
  const {
    roomForm: { trigger, watch, setValue },
  } = useCreateNewRoomDataContext();

  const [watchStartDate, watchEndDate, watchStartTime, watchEndTime, watchTimeZone] = watch([
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'timeZone',
  ]);
  const isStartDate = dateName === 'startDate';
  const otherDateField = isStartDate ? 'endDate' : 'startDate';
  const watchOtherDateField = isStartDate ? watchEndDate : watchStartDate;

  const { minimumAllowedTime, minimumAllowedDate } = getMinimumAllowedDateAndTime({
    isStartDate,
    watchStartDate,
    watchEndDate,
    watchStartTime,
    watchTimeZone,
  });

  const triggerRelatedFields = () => trigger(['startDate', 'startTime', 'endDate', 'endTime', 'reminders']);

  const onDateChange = (onChange: (event?: Date) => void) => (newDate?: Date) => {
    const shouldUpdateDateField =
      !watchOtherDateField || (dateName === 'startDate' && moment(watchEndDate).isBefore(newDate));
    if (shouldUpdateDateField) setValue(otherDateField, newDate, { shouldDirty: true });

    onChange(newDate);
    triggerRelatedFields();
  };

  const onTimeChange = (onChange: (event?: Date) => void) => (newTime?: Date) => {
    onChange(newTime);
    triggerRelatedFields();
  };

  const setEndTime = (time?: Date) => {
    const timeToUse = time || watchStartTime;
    if (!timeToUse || watchEndTime || timeName === 'endTime') return;

    const startTimePlusOneHour = new Date(timeToUse.getTime() + 60 * 60 * 1000);
    setValue('endTime', startTimePlusOneHour, { shouldDirty: true });
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-size-sm text-black">{label}</span>
      <div className="flex gap-2">
        <Controller
          name={dateName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DayInput
              name={dateName}
              value={value}
              onChange={onDateChange(onChange)}
              placeholder={t('date')}
              className="flex-1"
              disabledBeforeDate={minimumAllowedDate}
            />
          )}
        />
        <Controller
          name={timeName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <TimeInput
              name={timeName}
              value={value}
              onChange={onTimeChange(onChange)}
              onConfirm={setEndTime}
              placeholder={t('time')}
              disabledBeforeTime={minimumAllowedTime}
            />
          )}
        />
      </div>
    </div>
  );
};

export default DateTimeField;
